import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import InfoTip from '@components/charts/analytics/InfoTip'
import { ConditionalWrapper } from '@supplement-bacon/react-toolkit/utils'
import {
  Card,
  Flex,
  Skeleton,
  Space,
  Statistic,
  theme,
  Tooltip,
  Typography,
} from 'antd'
import { ReactNode } from 'react'

const { Text } = Typography

/**
 * `default`: antd `Statistic` card of the user/venue/list pages.
 * `overview`: membership analytics overview card (Figma 799-904).
 * `tile`: analytics drill-down tile (Figma 798-4008, 829-1588).
 */
type StatisticVariant = 'default' | 'overview' | 'tile'

type StatisticDeltaUnit = 'percent' | 'points' | 'absolute'

/** Semantic colour of the `tile` value and of its trend. */
type StatisticTone = 'success' | 'info' | 'error' | 'neutral'

type StatisticHeaderLayout = 'spread' | 'inline'

interface StatisticDeltaProps {
  /** Delta value; the arrow follows its sign. Rendered per `unit`. */
  value?: number
  /** How `value` is rendered: '%' suffix (default), 'pts' suffix, or a signed absolute count. */
  unit?: StatisticDeltaUnit
  /** e.g. "vs last period", "vs N-1". */
  label?: ReactNode
  /**
   * `overview` variant only. Whether growing is an improvement (default
   * `true`). Set to `false` for metrics like churn or departures, where an
   * increase is a regression. The arrow always follows the sign (up = ↑,
   * down = ↓); only the colour follows the improvement (Figma v2.1).
   */
  higherIsBetter?: boolean
}

interface StatisticTrendProps {
  /** Signed percentage; the arrow follows the sign. */
  value?: number
  /** Free text shown next to the value instead of a percentage (e.g. "Critical"). */
  label?: ReactNode
}

/** The one delta formatter shared by every variant. */
const formatDeltaValue = (
  value: number,
  unit: StatisticDeltaUnit = 'percent'
) => {
  const sign = value > 0 ? '+' : ''
  // French decimal separator to match the Figma copy (e.g. "+8,4%").
  const formatted = `${sign}${String(value).replace('.', ',')}`
  switch (unit) {
    case 'points':
      return `${formatted} pts`
    case 'absolute':
      return formatted
    case 'percent':
    default:
      return `${formatted}%`
  }
}

interface BaseProps {
  title: ReactNode
  /** Info icon + tooltip rendered next to the title (distinct from `help`'s question-mark tooltip). */
  tooltip?: ReactNode
  isLoading?: boolean
}

interface DefaultStatisticProps extends BaseProps {
  variant?: 'default'
  value?: number | string
  help?: ReactNode
  prefix?: ReactNode
  suffix?: ReactNode
  align?: 'left' | 'center' | 'right'
  /** Optional delta line rendered under the value (arrow + value + label). Additive - omit to keep prior behavior. */
  delta?: StatisticDeltaProps
}

interface OverviewStatisticProps extends BaseProps {
  variant: 'overview'
  value?: ReactNode
  /**
   * `spread` (default): label left, info icon on the right edge (General KPIs).
   * `inline`: info icon right after the label (Renewal & retention row).
   */
  headerLayout?: StatisticHeaderLayout
  delta?: StatisticDeltaProps
  /**
   * Grey secondary lines under the value (e.g. "47 coupons used"). A string
   * may carry several lines separated by "\n". Rendered above the delta.
   */
  secondary?: ReactNode
  /** Overrides the value colour (e.g. red / orange attention counts). */
  valueColor?: string
  /** Colour of a 3px left border on the card (bucket tiles). */
  accent?: string
  /** Slot under the value block, e.g. the "See these renewals →" link. */
  footer?: ReactNode
}

interface TileStatisticProps extends BaseProps {
  variant: 'tile'
  value?: ReactNode
  tone?: StatisticTone
  trend?: StatisticTrendProps
  /** Small grey line under the value (e.g. why a money figure is unavailable). */
  caption?: ReactNode
  /** Slot pinned to the right edge, vertically centred (e.g. a gauge ring). */
  extra?: ReactNode
}

type AnalyticsStatisticProps =
  | DefaultStatisticProps
  | OverviewStatisticProps
  | TileStatisticProps

const DefaultStatistic = ({
  title,
  help,
  tooltip,
  value,
  prefix,
  suffix,
  isLoading,
  align = 'center',
  delta,
}: DefaultStatisticProps) => {
  const { token } = theme.useToken()

  const direction =
    typeof delta?.value === 'number'
      ? delta.value > 0
        ? 'up'
        : delta.value < 0
          ? 'down'
          : 'flat'
      : undefined

  const deltaColor =
    direction === 'up'
      ? token.colorSuccess
      : direction === 'down'
        ? token.colorError
        : token.colorTextSecondary

  return (
    <Card style={{ alignContent: align, textAlign: align, minHeight: 120 }}>
      <Statistic
        title={
          <ConditionalWrapper
            condition={!!tooltip}
            wrapper={(children) =>
              align === 'left' ? (
                // Left-aligned cards (e.g. General): label on the left, info
                // icon pushed to the right edge of the header (Figma 799-904).
                // Reserve 2 lines so a wrapping label (e.g. "Revenue per
                // Subscription") doesn't push the value down out of line with
                // its siblings - all card values stay on the same baseline.
                <Flex
                  justify="space-between"
                  align="flex-start"
                  style={{ width: '100%', minHeight: 44 }}
                >
                  {children}
                  <InfoTip
                    title={tooltip}
                    style={{ color: token.colorTextTertiary }}
                  />
                </Flex>
              ) : (
                <Space size={5}>
                  {children}
                  <InfoTip
                    title={tooltip}
                    style={{ color: token.colorTextTertiary }}
                  />
                </Space>
              )
            }
          >
            <ConditionalWrapper
              condition={!!help}
              wrapper={(children) => (
                <Tooltip title={help}>
                  <Space size={5}>
                    {children}
                    <QuestionCircleOutlined />
                  </Space>
                </Tooltip>
              )}
            >
              {title}
            </ConditionalWrapper>
          </ConditionalWrapper>
        }
        value={value}
        prefix={prefix}
        suffix={suffix}
        loading={isLoading}
      />
      {delta && !isLoading && (
        <Space
          size={4}
          style={{
            width: '100%',
            // Reserve the line height even when there is no delta value (the
            // API omits deltaPercent when there is no baseline) so cards stay
            // the same height.
            minHeight: 22,
            justifyContent:
              align === 'left'
                ? 'flex-start'
                : align === 'right'
                  ? 'flex-end'
                  : 'center',
          }}
        >
          {typeof delta.value === 'number' && (
            <>
              {direction === 'up' && (
                <ArrowUpOutlined style={{ color: deltaColor }} />
              )}
              {direction === 'down' && (
                <ArrowDownOutlined style={{ color: deltaColor }} />
              )}
              <Text style={{ color: deltaColor }}>
                {formatDeltaValue(delta.value, delta.unit)}
              </Text>
              {delta.label && (
                <Text style={{ color: deltaColor }}>{delta.label}</Text>
              )}
            </>
          )}
        </Space>
      )}
    </Card>
  )
}

/**
 * Membership analytics overview KPI card, matching Figma 799-904 exactly:
 * 20px padding, ~150px tall with vertically-centred content, 14px tertiary
 * label + info icon (`headerLayout`), 28px bold value, and a 12px semibold
 * delta line (arrow + coloured text) and/or a grey secondary line.
 */
const OverviewStatistic = ({
  title,
  tooltip,
  headerLayout = 'spread',
  value,
  isLoading,
  delta,
  secondary,
  valueColor,
  footer,
  accent,
}: OverviewStatisticProps) => {
  const { token } = theme.useToken()

  const deltaValue = typeof delta?.value === 'number' ? delta.value : undefined
  const hasDelta = deltaValue !== undefined
  const isImprovement = hasDelta
    ? (delta?.higherIsBetter ?? true)
      ? deltaValue > 0
      : deltaValue < 0
    : false
  // Arrow follows the sign; colour follows good / bad.
  const direction = hasDelta
    ? deltaValue === 0
      ? 'flat'
      : deltaValue > 0
        ? 'up'
        : 'down'
    : undefined
  const deltaColor =
    direction === 'flat'
      ? token.colorTextTertiary
      : isImprovement
        ? token.colorSuccess
        : token.colorError

  return (
    <Card
      // `height: 100%` only bites in a `Row align="stretch"`, where the card
      // grows to the tallest sibling (Figma 800-1199). The flex column on the
      // card + `flex: 1` on its body are what make that extra height reach the
      // content: `.ant-card-body` is content-height by default, so without them
      // the content stays stuck at the top of a stretched card.
      style={{
        minHeight: 150,
        height: '100%',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        ...(accent && { borderLeft: `3px solid ${accent}` }),
      }}
      styles={{
        body: {
          padding: 20,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/*
        Vertically centred, as Figma 800-1189 measures it (51px of slack above
        the content): in a row, that lands the secondary line on the neighbouring
        repartition legend and the 28px value across its bar. Cards of the same
        row line up because the 15px delta/secondary line below is always
        reserved, so they all centre the same content height.
      */}
      <Flex
        vertical
        justify="flex-start"
        gap={12}
        style={{ flex: 1, minHeight: 110 }}
      >
        <Flex
          align="center"
          justify={headerLayout === 'inline' ? 'flex-start' : 'space-between'}
          gap={headerLayout === 'inline' ? 4 : 8}
        >
          <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>
            {title}
          </Text>
          {tooltip && (
            <InfoTip
              title={tooltip}
              style={{
                color: token.colorTextTertiary,
                fontSize: 16,
                flexShrink: 0,
              }}
            />
          )}
        </Flex>
        <Flex vertical gap={4} align="flex-start" style={{ width: '100%' }}>
          {isLoading ? (
            <Skeleton.Input active size="large" style={{ width: 140 }} />
          ) : (
            <Text
              style={{
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1.2,
                color: valueColor ?? token.colorText,
              }}
            >
              {value ?? '--'}
            </Text>
          )}
          {/*
            Always rendered, even with nothing to show: reserving this 15px line
            keeps the label and the value at the same height on every card of a
            row, whether or not the endpoint returns a delta.
          */}
          <Flex vertical gap={4} style={{ minHeight: 15 }}>
            {!isLoading && (
              <>
                {secondary && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: token.colorTextTertiary,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {secondary}
                  </Text>
                )}
                {hasDelta && (
                  <Space size={4}>
                    {direction === 'up' && (
                      <ArrowUpOutlined
                        style={{ color: deltaColor, fontSize: 10 }}
                      />
                    )}
                    {direction === 'down' && (
                      <ArrowDownOutlined
                        style={{ color: deltaColor, fontSize: 10 }}
                      />
                    )}
                    <Text
                      style={{
                        color: deltaColor,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {formatDeltaValue(deltaValue, delta?.unit)}
                      {delta?.label ? ` ${delta.label}` : ''}
                    </Text>
                  </Space>
                )}
              </>
            )}
          </Flex>
          {footer && !isLoading && <div style={{ marginTop: 8 }}>{footer}</div>}
        </Flex>
      </Flex>
    </Card>
  )
}

/**
 * KPI tile of the analytics drill-down pages (Figma 798-4008, 829-1588): 14px
 * tertiary label, 24px value in a semantic colour, optional trend on the same
 * baseline and an optional `extra` slot on the right. The label/value stack is
 * vertically centred so that, in a stretched row, every tile lines its label up
 * on one line and its value on the next - including next to a taller `extra`.
 * Distinct from the `overview` variant, whose value is 28px with the delta on
 * its own line.
 */
const TileStatistic = ({
  title,
  value,
  tone = 'neutral',
  isLoading,
  trend,
  tooltip,
  extra,
  caption,
}: TileStatisticProps) => {
  const { token } = theme.useToken()

  const toneColor = {
    success: token.colorSuccess,
    // The theme repaints `colorInfo` with the brand green, so the designed blue
    // has to be literal here.
    info: '#1677ff',
    error: token.colorError,
    neutral: token.colorText,
  }[tone]

  const trendStyle = { color: toneColor, fontSize: 12, fontWeight: 600 }

  return (
    <Card
      // The flex column on the card plus `flex: 1` on its body is what lets a
      // stretched card push its extra height down to the content, which is
      // what keeps labels and values aligned across a row of tiles.
      style={{
        height: '100%',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
      }}
      styles={{
        body: {
          padding: 20,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Flex align="center" justify="space-between" gap={12} style={{ flex: 1 }}>
        <Flex vertical gap={8} align="flex-start">
          <Flex align="center" gap={4}>
            <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>
              {title}
            </Text>
            {tooltip && (
              <InfoTip
                title={tooltip}
                style={{ color: token.colorTextTertiary, fontSize: 16 }}
              />
            )}
          </Flex>
          {isLoading ? (
            <Skeleton.Input active size="small" style={{ width: 120 }} />
          ) : (
            <Flex align="baseline" gap={12}>
              <Text
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: toneColor,
                }}
              >
                {value ?? '--'}
              </Text>
              {trend && (
                <Flex align="center" gap={4}>
                  {typeof trend.value === 'number' && (
                    <>
                      {trend.value < 0 ? (
                        <ArrowDownOutlined
                          style={{ color: toneColor, fontSize: 10 }}
                        />
                      ) : (
                        <ArrowUpOutlined
                          style={{ color: toneColor, fontSize: 10 }}
                        />
                      )}
                      <Text style={trendStyle}>
                        {formatDeltaValue(trend.value, 'percent')}
                      </Text>
                    </>
                  )}
                  {trend.label && <Text style={trendStyle}>{trend.label}</Text>}
                </Flex>
              )}
            </Flex>
          )}
          {caption && !isLoading && (
            <Text
              style={{
                fontSize: 12,
                color: token.colorTextTertiary,
                whiteSpace: 'pre-line',
              }}
            >
              {caption}
            </Text>
          )}
        </Flex>
        {extra}
      </Flex>
    </Card>
  )
}

/**
 * The single analytics KPI card. `variant` picks the layout; the props of the
 * other variants are rejected by the compiler, so a card can only be built out
 * of the pieces its own design has.
 */
const AnalyticsStatistic = (props: AnalyticsStatisticProps) => {
  switch (props.variant) {
    case 'overview':
      return <OverviewStatistic {...props} />
    case 'tile':
      return <TileStatistic {...props} />
    default:
      return <DefaultStatistic {...props} />
  }
}

type AnalyticsStatisticType = { Statistic: typeof AnalyticsStatistic }
export { AnalyticsStatistic, type AnalyticsStatisticType }
export type {
  AnalyticsStatisticProps,
  DefaultStatisticProps,
  OverviewStatisticProps,
  StatisticDeltaProps,
  StatisticDeltaUnit,
  StatisticHeaderLayout,
  StatisticTone,
  StatisticTrendProps,
  StatisticVariant,
  TileStatisticProps,
}
